import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CurriculumTable = ({ readings, onEdit, onDelete, onAdd }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('day');
  const [sortOrder, setSortOrder] = useState('asc');

  const filteredReadings = readings?.filter(reading =>
    reading?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    reading?.content?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const sortedReadings = [...filteredReadings]?.sort((a, b) => {
    const aValue = sortBy === 'day' ? a?.day : a?.title?.toLowerCase();
    const bValue = sortBy === 'day' ? b?.day : b?.title?.toLowerCase();
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const truncateContent = (content, maxLength = 100) => {
    if (content?.length <= maxLength) return content;
    return content?.substring(0, maxLength) + '...';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-gentle">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Curriculum Management
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage the 42-day reading curriculum content
            </p>
          </div>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={onAdd}
          >
            Add Reading
          </Button>
        </div>

        {/* Search and Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mt-4">
          <div className="relative flex-1">
            <Icon 
              name="Search" 
              size={16} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder="Search readings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="text-sm text-muted-foreground flex items-center">
            {sortedReadings?.length} of {readings?.length} readings
          </div>
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('day')}
                  className="flex items-center space-x-1 font-caption font-medium text-foreground hover:text-primary transition-colors"
                >
                  <span>Day</span>
                  <Icon 
                    name={sortBy === 'day' && sortOrder === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                    size={14}
                    className={sortBy === 'day' ? 'text-primary' : 'text-muted-foreground'}
                  />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('title')}
                  className="flex items-center space-x-1 font-caption font-medium text-foreground hover:text-primary transition-colors"
                >
                  <span>Title</span>
                  <Icon 
                    name={sortBy === 'title' && sortOrder === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                    size={14}
                    className={sortBy === 'title' ? 'text-primary' : 'text-muted-foreground'}
                  />
                </button>
              </th>
              <th className="text-left p-4 font-caption font-medium text-foreground">Content Preview</th>
              <th className="text-left p-4 font-caption font-medium text-foreground">Status</th>
              <th className="text-right p-4 font-caption font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedReadings?.map((reading) => (
              <tr key={reading?.id} className="border-t border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-primary/10 text-primary rounded-lg font-heading font-semibold text-sm">
                    {reading?.day}
                  </div>
                </td>
                <td className="p-4">
                  <h3 className="font-caption font-medium text-foreground">
                    {reading?.title}
                  </h3>
                </td>
                <td className="p-4">
                  <p className="text-sm text-muted-foreground max-w-md">
                    {truncateContent(reading?.content)}
                  </p>
                </td>
                <td className="p-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    reading?.isPublished 
                      ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                  }`}>
                    {reading?.isPublished ? 'Published' : 'Draft'}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => onEdit(reading)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => onDelete(reading)}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden p-4 space-y-4">
        {sortedReadings?.map((reading) => (
          <div key={reading?.id} className="bg-muted/30 rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 text-primary rounded-lg font-heading font-semibold">
                  {reading?.day}
                </div>
                <div>
                  <h3 className="font-caption font-medium text-foreground">
                    {reading?.title}
                  </h3>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                    reading?.isPublished 
                      ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                  }`}>
                    {reading?.isPublished ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground">
              {truncateContent(reading?.content, 150)}
            </p>
            
            <div className="flex items-center space-x-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Edit"
                iconPosition="left"
                onClick={() => onEdit(reading)}
                fullWidth
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Trash2"
                iconPosition="left"
                onClick={() => onDelete(reading)}
                fullWidth
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {sortedReadings?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="BookOpen" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-heading font-medium text-foreground mb-2">
            {searchTerm ? 'No readings found' : 'No readings yet'}
          </h3>
          <p className="text-muted-foreground mb-4">
            {searchTerm 
              ? 'Try adjusting your search terms' :'Start by adding your first daily reading'
            }
          </p>
          {!searchTerm && (
            <Button variant="default" iconName="Plus" iconPosition="left" onClick={onAdd}>
              Add First Reading
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default CurriculumTable;